// SPDX-License-Identifier: SEE LICENSE
import { shortHash } from '../lib/format';
import type { PillarMeta } from '../lib/pillars';
import type { StepRecord } from '../lib/types';

export function ModelCard({
  meta,
  verdict,
  step,
}: {
  meta: PillarMeta;
  verdict: Record<string, unknown> | undefined;
  step: StepRecord | undefined;
}) {
  const detail = (step?.summary ?? {}) as Record<string, unknown>;
  const runId = (step?.runId ?? (verdict?.runId as string | undefined)) ?? undefined;
  const reportSha =
    (detail.reportSha256Hex as string | undefined) ??
    (detail.report_sha256_hex as string | undefined);
  const attested =
    (detail.attestationVerified as boolean | undefined) ??
    (detail.attestation_verified as boolean | undefined);

  return (
    <div className="space-y-4 p-5">
      <Section title="Model / algorithm">
        <div className="text-sm text-slate-800">{meta.model}</div>
        <ul className="mt-2 space-y-1.5">
          {meta.modelCard.map((line, i) => (
            <li key={i} className="flex gap-2 text-[12px] leading-relaxed text-slate-600">
              <span className="mt-1.5 h-1 w-1 flex-none rounded-full" style={{ background: meta.accent }} />
              {line}
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Where it runs">
        <div className="flex flex-wrap gap-2">
          <Chip label={meta.runsOn === 'on-device' ? 'On-device inference' : 'On-device + server recompute'} accent={meta.accent} />
          <Chip label="Raw telemetry never leaves device" accent="#5fd0a8" />
        </div>
      </Section>

      <Section title="Cryptographic binding">
        <div className="text-[12px] leading-relaxed text-slate-600">{meta.crypto}</div>
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          <KV k="Run ID" v={runId ? shortHash(runId, 10, 6) : '—'} mono />
          <KV
            k="Attestation"
            v={attested === undefined ? 'pending' : attested ? 'verified' : 'unverified (dev)'}
            {...(attested ? { color: '#5fd0a8' } : attested === false ? { color: '#f0a35e' } : {})}
          />
          <KV k="Report SHA-256" v={shortHash(reportSha, 12, 8)} mono full />
        </div>
      </Section>

      <Section title="Data flow proven on the dashboard">
        <ol className="space-y-1.5 text-[12px] text-slate-600">
          <Flow n={1} text="Device captures signals on-device (sensors, camera, audio)." />
          <Flow n={2} text="Model runs locally → derived metrics + verdict (this panel)." />
          <Flow n={3} text="Verdict canonical-hashed → fresh TEE EC P-256 key signs it." />
          <Flow n={4} text="Signed run + derived metrics POSTed; server recomputes & verifies the chain." />
          <Flow n={5} text="Leaf folded into the session Merkle root → composite certificate." />
        </ol>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="eyebrow mb-2">{title}</div>
      {children}
    </div>
  );
}

function Chip({ label, accent }: { label: string; accent: string }) {
  return (
    <span
      className="pill"
      style={{ color: accent, background: `${accent}1a`, border: `1px solid ${accent}33` }}
    >
      {label}
    </span>
  );
}

function KV({
  k,
  v,
  mono,
  full,
  color,
}: {
  k: string;
  v: string;
  mono?: boolean;
  full?: boolean;
  color?: string;
}) {
  return (
    <div className={`rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 ${full ? 'sm:col-span-2' : ''}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{k}</div>
      <div className={`mt-0.5 text-sm ${mono ? 'font-mono' : ''}`} style={{ color: color ?? '#1e293b' }}>
        {v}
      </div>
    </div>
  );
}

function Flow({ n, text }: { n: number; text: string }) {
  return (
    <li className="flex gap-2">
      <span className="flex h-4 w-4 flex-none items-center justify-center rounded-full bg-slate-100 text-[9px] font-bold text-slate-600">
        {n}
      </span>
      {text}
    </li>
  );
}
